import React, { useState } from 'react';
import { FormRendererAPIType } from 'components/common/form';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { Popup } from 'components/common/popup/Popup';
import FormText from 'components/common/form/layout/control/FormText';
import MITable from 'components/common/grid/MITable';
import { useTranslation } from 'react-i18next';
import { AlertRuleScreenProps } from 'components/alert-rule-popup/index';
import { alertRuleAPI } from 'api/alert-wizard';

interface AlertRuleSendTestConfirmPopupProps extends AlertRuleScreenProps {
    onClose: () => void;
    confirmData: any;
}

//ToDo Merge with component (unify)
//frontend/src/app/notification/send-now/SendToGroup.tsx
export default function AlertRuleSendTestConfirmPopup(props: AlertRuleSendTestConfirmPopupProps) {
    const { alertRuleData, confirmData, onClose = () => {} } = props;
    const { t } = useTranslation(['components/alert-rule-popup/alert_rule_popup']);

    const [sendToType, setSendToType] = useState<string>('me');
    const [userEmail, setUserEmail] = useState<string>('');
    const [recipientsData, setRecipientsData] = useState<any>();
    const [showRecipientsPopup, setShowRecipientsPopup] = useState<boolean>(false);

    const sendTestAction = () => {
        onClose();
        setUserEmail('');
        alertRuleAPI
            .sendTest(
                { ...alertRuleData, metric_id: alertRuleData.element_id },
                alertRuleData.preview_for,
                sendToType,
                userEmail,
            )
            .then((response) => {
                if (response.data.status == 'OK') {
                    alert(response.data.messages.sent ?? response.data.messages.notSent);
                }
            })
            .catch(() => {});
    };

    const showRecipientsList = (event: any, sendTo: string) => {
        event.preventDefault();

        alertRuleAPI
            .getSendTestRecipientsList(
                sendTo,
                alertRuleData.preview_for,
                Number(alertRuleData.alert_rule_id),
                alertRuleData.element_id,
            )
            .then((response) => {
                if (200 == response?.status) {
                    setRecipientsData(response.data);
                    setShowRecipientsPopup(true);
                }
            })
            .catch(() => {});
    };

    return (
        <>
            <Popup
                settings={{
                    title: t('send_test_confirm_popup.title'),
                    textOK: t('send_test_confirm_popup.ok_btn'),
                }}
                onHide={() => {
                    onClose();
                }}
                onConfirm={sendTestAction}
                open
                maxWidth={'popupMd'}
            >
                <Box>
                    <FormControl sx={{ width: '100%' }}>
                        <RadioGroup
                            name="send-to"
                            onChange={(newVal) => {
                                setSendToType(newVal.target.value);
                            }}
                            value={String(sendToType)}
                        >
                            <FormControlLabel
                                value="me"
                                control={<Radio />}
                                label={t('send_test_confirm_popup.label_me', { mail: confirmData.email })}
                            />
                            {confirmData.adminsCount > 0 && (
                                <FormControlLabel
                                    value="admins"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            {t('send_test_confirm_popup.label_admins')}{' '}
                                            <Box
                                                component={'a'}
                                                href="#"
                                                onClick={(event) => {
                                                    showRecipientsList(event, 'admins');
                                                }}
                                            >
                                                {t('send_test_confirm_popup.label_admins_link', {
                                                    amount: confirmData.adminsCount,
                                                })}
                                            </Box>
                                        </Box>
                                    }
                                />
                            )}
                            {confirmData.recipientsCount > 0 && (
                                <FormControlLabel
                                    value="all"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            {t('send_test_confirm_popup.label_all')}{' '}
                                            <Box
                                                component={'a'}
                                                href="#"
                                                onClick={(event) => {
                                                    showRecipientsList(event, 'recipients');
                                                }}
                                            >
                                                {t('send_test_confirm_popup.label_all_link', {
                                                    amount: confirmData.recipientsCount,
                                                })}
                                            </Box>
                                        </Box>
                                    }
                                />
                            )}
                            {alertRuleData.preview_for != 'push' && (
                                <FormControlLabel
                                    value="other"
                                    control={<Radio />}
                                    label={
                                        <Stack direction={'row'} alignItems={'center'}>
                                            <Box sx={{ whiteSpace: 'nowrap', mr: '5px' }}>
                                                {t('send_test_confirm_popup.label_other')}
                                            </Box>
                                            <FormControl sx={{ width: '300px' }}>
                                                <FormText
                                                    controlProps={{
                                                        form: {} as FormRendererAPIType,
                                                        name: 'user_email',
                                                        onChange: (val) => {
                                                            setUserEmail(val);
                                                        },
                                                        placeholder: confirmData.email,
                                                        value: userEmail,
                                                    }}
                                                />
                                            </FormControl>
                                        </Stack>
                                    }
                                />
                            )}
                        </RadioGroup>
                    </FormControl>

                    <Box sx={{ mt: 2 }}>{t('send_test_confirm_popup.note')}</Box>
                </Box>
            </Popup>

            {showRecipientsPopup && (
                <Popup
                    settings={{
                        title: t('recipients_popup.title'),
                        noCancel: true,
                        textOK: t('recipients_popup.ok_btn'),
                        customMaxWidth: '718px',
                    }}
                    open={true}
                    onHide={() => {
                        setShowRecipientsPopup(false);
                    }}
                    onConfirm={() => {
                        setShowRecipientsPopup(false);
                    }}
                >
                    <Box>
                        <MITable
                            autoHeight
                            gridName={'recipients_popup.grid_name'}
                            massActionsConfig={{ enabled: false }}
                            data={(recipientsData ?? []).map((user: any) => {
                                return {
                                    name: user.display_name,
                                    email: user.email,
                                };
                            })}
                            height={200}
                            columns={[
                                {
                                    name: 'name',
                                    title: t('recipients_popup.grid_cell_name'),
                                    resizable: false,
                                },
                                {
                                    name: 'email',
                                    title: t('recipients_popup.grid_cell_email'),
                                    resizable: false,
                                },
                            ]}
                        />
                    </Box>
                </Popup>
            )}
        </>
    );
}
